import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  CircleHelp,
  Gauge,
  RotateCcw,
  ShieldAlert,
  Stethoscope,
  XCircle,
} from "lucide-react";
import { fetchNode } from "@/faults-app/lib/db";
import { AppShell, StatusBadge, severityToneKey } from "@/faults-app/components/app-shell";
import { faultIcon } from "@/faults-app/components/electric-icons";

export const Route = createFileRoute("/diagnose")({
  validateSearch: (s: Record<string, unknown>): { node: string } => ({
    node: typeof s['node'] === "string" ? s['node'] : "motor_not_running",
  }),
  head: () => ({
    meta: [
      { title: "التشخيص التفاعلي للأعطال الكهربائية" },
      {
        name: "description",
        content:
          "أسئلة تشخيص عربية خطوة بخطوة (نعم / لا / لا أعرف) للوصول إلى السبب الأقرب للعطل الكهربائي.",
      },
      { property: "og:title", content: "التشخيص التفاعلي للأعطال الكهربائية" },
      {
        property: "og:description",
        content: "تشخيص تفاعلي بالعربي مع شرح طريقة القياس وتحذيرات السلامة.",
      },
    ],
  }),
  component: Diagnose,
});

function Diagnose() {
  const { node } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [showHelp, setShowHelp] = useState(false);
  const [reading, setReading] = useState("");
  const [answers, setAnswers] = useState<{ q: string; a: string }[]>([]);

  const { data, isLoading } = useQuery({
    queryKey: ["node", node],
    queryFn: () => fetchNode(node),
  });

  const go = (next: string | null, answer: string) => {
    if (!data) return;
    setAnswers((prev) => [...prev, { q: data.question_ar, a: answer }]);
    setShowHelp(false);
    setReading("");
    if (next) navigate({ search: { node: next } });
  };

  if (isLoading)
    return (
      <AppShell>
        <p className="p-10 text-center text-muted-foreground">جارٍ التحميل...</p>
      </AppShell>
    );
  if (!data)
    return (
      <AppShell>
        <p className="p-10 text-center">لم يتم العثور على خطوة التشخيص.</p>
      </AppShell>
    );

  const isResult = Boolean(data.result_ar);
  const total = Math.max(answers.length + (isResult ? 0 : 1), 1);
  const done = isResult ? total : answers.length;
  const pct = Math.round(((isResult ? total : answers.length) / Math.max(total, 1)) * 100);
  const QIcon = faultIcon(`${data.title_ar ?? ""} ${data.question_ar}`);
  const top = [...data.result_causes].sort((a, b) => b.probability - a.probability)[0];

  return (
    <AppShell>
      <main className="mx-auto w-full max-w-3xl px-4 pt-6">
        <header className="rounded-3xl panel-dark p-5 shadow-card">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary text-primary-foreground">
              <Stethoscope className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <h1 className="truncate text-xl font-extrabold text-white">تشخيص العطل</h1>
              <p className="truncate text-sm text-primary">{data.title_ar ?? "مسار تشخيص"}</p>
            </div>
          </div>
          <div className="mt-4">
            <div className="mb-1 flex items-center justify-between text-xs font-bold text-white/70">
              <span>{isResult ? "اكتملت خطوات التشخيص" : `الخطوة ${done + 1} من ${total + 1}`}</span>
              <span>{pct}%</span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-white/15">
              <div
                className="h-full rounded-full bg-linear-to-l from-primary-dark to-primary-glow transition-all duration-500"
                style={{ width: `${Math.max(pct, 8)}%` }}
              />
            </div>
          </div>
        </header>

        {answers.length > 0 && (
          <ol className="mt-4 space-y-1 rounded-2xl border border-border bg-card p-3 text-xs text-muted-foreground shadow-card">
            {answers.map((a, i) => (
              <li key={i} className="flex gap-2">
                <span className="font-bold text-primary-dark">{i + 1}.</span>
                <span className="min-w-0 flex-1">{a.q}</span>
                <span className="shrink-0 font-extrabold text-foreground">{a.a}</span>
              </li>
            ))}
          </ol>
        )}

        {!isResult ? (
          <section className="mt-4 card-tech p-5">
            <h2 className="flex items-start gap-3 text-lg font-extrabold leading-relaxed sm:text-xl">
              <QIcon className="mt-1 h-6 w-6 shrink-0 text-primary-dark" />
              <span>{data.question_ar}</span>
            </h2>

            {data.safety_ar && (
              <p className="mt-3 flex gap-2 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm font-bold text-destructive">
                <ShieldAlert className="h-5 w-5 shrink-0" />
                <span>تحذير سلامة: {data.safety_ar}</span>
              </p>
            )}

            <div className="mt-5 grid gap-3">
              <button
                onClick={() => go(data.yes_next, "نعم")}
                className="press flex items-center justify-center gap-2 rounded-2xl bg-success px-4 py-4 text-lg font-extrabold text-success-foreground shadow-card transition hover:brightness-95"
              >
                <CheckCircle2 className="h-6 w-6" /> نعم
              </button>
              <button
                onClick={() => go(data.no_next, "لا")}
                className="press flex items-center justify-center gap-2 rounded-2xl bg-destructive px-4 py-4 text-lg font-extrabold text-destructive-foreground shadow-card transition hover:brightness-95"
              >
                <XCircle className="h-6 w-6" /> لا
              </button>
              <button
                onClick={() => setShowHelp(true)}
                className="press flex items-center justify-center gap-2 rounded-2xl border-2 border-primary bg-card px-4 py-4 text-lg font-extrabold text-primary-dark transition hover:bg-accent"
              >
                <CircleHelp className="h-6 w-6" /> لا أعرف
              </button>
            </div>

            {showHelp && (
              <div className="mt-4 animate-fade-in rounded-2xl border border-primary/40 bg-accent/50 p-4">
                <p className="flex items-center gap-2 text-base font-extrabold text-accent-foreground">
                  <Gauge className="h-5 w-5" /> طريقة الفحص
                </p>
                <p className="mt-2 text-sm leading-relaxed">{data.explain_ar}</p>

                <div className="mt-3 rounded-xl border border-dashed border-primary/50 bg-card p-3">
                  <div className="flex items-center justify-between text-xs font-bold text-muted-foreground">
                    <span>نقطة القياس ١</span>
                    <span className="mx-2 h-px flex-1 bg-linear-to-l from-primary to-primary-glow" />
                    <span>نقطة القياس ٢</span>
                  </div>
                  <p className="mt-2 text-center text-xs text-muted-foreground">
                    اضبط جهاز القياس على الوضع المناسب (AC/DC Voltage أو Continuity) قبل القياس.
                  </p>
                </div>

                <label className="mt-3 block text-sm font-bold" htmlFor="reading">
                  أدخل القراءة
                </label>
                <input
                  id="reading"
                  value={reading}
                  onChange={(e) => setReading(e.target.value)}
                  inputMode="decimal"
                  placeholder="مثال: 220V أو 0V"
                  className="mt-1 w-full rounded-xl border border-input bg-card p-3 outline-none focus:border-primary focus:ring-3 focus:ring-primary/25"
                />

                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <button
                    onClick={() => go(data.yes_next, reading ? `تم القياس: ${reading}` : "تم القياس - يوجد")}
                    className="press rounded-xl bg-success px-4 py-3 font-extrabold text-success-foreground"
                  >
                    تم القياس — النتيجة نعم
                  </button>
                  <button
                    onClick={() => go(data.no_next, reading ? `تم القياس: ${reading}` : "تم القياس - لا يوجد")}
                    className="press rounded-xl bg-destructive px-4 py-3 font-extrabold text-destructive-foreground"
                  >
                    تم القياس — النتيجة لا
                  </button>
                </div>
              </div>
            )}
          </section>
        ) : (
          <section className="mt-4 space-y-4">
            <div className="overflow-hidden rounded-3xl border-2 border-primary bg-card shadow-glow">
              <div className="flex items-center gap-2 bg-linear-to-l from-primary-dark to-primary px-4 py-2 text-sm font-extrabold text-primary-foreground">
                <AlertTriangle className="h-5 w-5" /> العطل الأكثر احتمالاً
              </div>
              <div className="p-5">
                <h2 className="text-xl font-extrabold leading-relaxed">{data.result_ar}</h2>
                {top && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-sm font-bold">
                      <span>نسبة الاحتمال</span>
                      <span className="text-primary-dark">{top.probability}%</span>
                    </div>
                    <div className="mt-1 h-3 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-linear-to-l from-primary-dark to-primary-glow"
                        style={{ width: `${top.probability}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                  <InfoTile label="مكان العطل" value={data.title_ar ?? "الدائرة الكهربائية"} />
                  <InfoTile label="نوع العطل" value={top?.cause ?? "غير محدد"} />
                  <InfoTile label="درجة الخطورة" value={data.severity ?? "غير محددة"} />
                </div>
                {data.severity && (
                  <div className="mt-3">
                    <StatusBadge tone={severityToneKey(data.severity)}>
                      <AlertTriangle className="h-3.5 w-3.5" /> درجة الخطورة: {data.severity}
                    </StatusBadge>
                  </div>
                )}
              </div>
            </div>

            <div className="card-tech p-5">
              <h3 className="mb-3 text-base font-extrabold">الأسباب المحتملة ودرجة الاحتمال</h3>
              <ul className="space-y-3">
                {data.result_causes.map((c, i) => (
                  <li key={i}>
                    <div className="flex justify-between text-sm font-bold">
                      <span className="min-w-0 pe-2">{c.cause}</span>
                      <span className="shrink-0 text-primary-dark">{c.probability}%</span>
                    </div>
                    <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${c.probability}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* مسار الفحص كمخطط بصري */}
            <div className="card-tech p-5">
              <h3 className="mb-3 text-base font-extrabold">افحص بالترتيب — مسار الدائرة</h3>
              <ol className="space-y-2">
                {data.result_steps.map((s, i) => {
                  const Ico = faultIcon(s);
                  const tone =
                    i === 0
                      ? "border-warning/50 bg-warning/10"
                      : i === data.result_steps.length - 1
                        ? "border-destructive/40 bg-destructive/10"
                        : "border-border bg-muted/40";
                  return (
                    <li key={i}>
                      <div className={`flex items-center gap-3 rounded-2xl border p-3 ${tone}`}>
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-card text-primary-dark">
                          <Ico className="h-5 w-5" />
                        </span>
                        <span className="min-w-0 flex-1 text-sm font-bold">{s}</span>
                        <span className="shrink-0 rounded-full bg-card px-2 py-0.5 text-xs font-bold text-muted-foreground">
                          {i + 1}
                        </span>
                      </div>
                      {i < data.result_steps.length - 1 && (
                        <div className="my-1 flex justify-center text-primary">↓</div>
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>

            {data.result_solution_ar && (
              <div className="card-tech border-success/40 p-5">
                <h3 className="mb-2 flex items-center gap-2 text-base font-extrabold text-success">
                  <CheckCircle2 className="h-5 w-5" /> الحل المقترح
                </h3>
                <p className="text-sm leading-relaxed">{data.result_solution_ar}</p>
              </div>
            )}

            {data.safety_ar && (
              <p className="flex gap-2 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm font-bold text-destructive">
                <ShieldAlert className="h-5 w-5 shrink-0" />
                <span>تحذير سلامة: {data.safety_ar}</span>
              </p>
            )}

            <div className="grid gap-2 sm:grid-cols-2">
              <Link
                to="/"
                className="press inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-4 font-extrabold text-primary-foreground shadow-glow transition hover:bg-primary-dark hover:text-white"
              >
                <RotateCcw className="h-5 w-5" /> تشخيص جديد
              </Link>
              <Link
                to="/library"
                search={{ cat: undefined, q: "" }}
                className="press inline-flex items-center justify-center gap-2 rounded-2xl border border-steel/30 bg-card px-5 py-4 font-extrabold transition hover:border-primary"
              >
                مكتبة الأعطال <ArrowLeft className="h-5 w-5" />
              </Link>
            </div>
          </section>
        )}
      </main>
    </AppShell>
  );
}

function InfoTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border bg-muted/50 p-3">
      <p className="text-[11px] font-bold text-muted-foreground">{label}</p>
      <p className="mt-1 line-clamp-2 text-sm font-extrabold">{value}</p>
    </div>
  );
}
